import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { FlowStepDefinition, ReloadResetConfig } from '../types/config';

const normalizePath = (path: string): string => {
  if (!path) return '';
  return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
};

const isPageReload = (): boolean => {
  try {
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return navigation?.type === 'reload';
  } catch {
    return false;
  }
};

const resolveStepIndexFromPath = (
  pathname: string,
  basePath: string,
  flowSteps: FlowStepDefinition[],
): number => {
  const normalizedPathname = normalizePath(pathname);
  const normalizedBase = normalizePath(basePath);

  let suffix = '';
  if (normalizedPathname === normalizedBase) {
    suffix = '';
  } else if (normalizedPathname.startsWith(`${normalizedBase}/`)) {
    suffix = normalizedPathname.slice(normalizedBase.length);
  } else {
    return 0;
  }

  const index = flowSteps.findIndex((step) => step.path === suffix);
  return index >= 0 ? index : 0;
};

const resolveReloadResetIndex = (
  pathname: string,
  basePath: string,
  flowSteps: FlowStepDefinition[],
  reloadReset: ReloadResetConfig,
): number | null => {
  if (!isPageReload()) return null;

  const urlStepIndex = resolveStepIndexFromPath(pathname, basePath, flowSteps);
  const urlStepKey = flowSteps[urlStepIndex]?.key;
  if (urlStepKey && reloadReset.excludeStepKeys?.includes(urlStepKey)) {
    return null;
  }

  const resetIndex = flowSteps.findIndex((step) => step.key === reloadReset.toStepKey);
  return resetIndex >= 0 ? resetIndex : null;
};

type ResolveInitialStepIndex = (urlStepIndex: number, isReload: boolean) => number;

const useStepRouteInit = (
  basePath: string,
  flowSteps: FlowStepDefinition[],
  reloadReset?: ReloadResetConfig,
  resolveInitialStepIndex?: ResolveInitialStepIndex,
): number => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const urlStepIndex = resolveStepIndexFromPath(pathname, basePath, flowSteps);

    if (resolveInitialStepIndex) {
      return resolveInitialStepIndex(urlStepIndex, isPageReload());
    }

    if (reloadReset) {
      const resetIndex = resolveReloadResetIndex(pathname, basePath, flowSteps, reloadReset);
      if (resetIndex !== null) return resetIndex;
    }

    return urlStepIndex;
  }, [pathname, basePath, flowSteps, reloadReset, resolveInitialStepIndex]);
};

export {
  resolveStepIndexFromPath,
  isPageReload,
  resolveReloadResetIndex,
};
export default useStepRouteInit;
